<script setup lang="ts">
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { fullName, getLatestUpdatedStatus } from '@helpers/utils';
import { Dossier } from '@graphql/types/dossiers';
import { FinancingType } from '@graphql/enums/dossiers';
import { useRouter } from 'vue-router';
import { Property } from '@graphql/types/properties';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

dayjs.locale('nl');
dayjs.extend(duration);
dayjs.extend(relativeTime);

export interface Props {
  dossier: Dossier;
  isRowActive: boolean;
  index: number;
}

interface Emits {
  (event: 'toggle:isRowActive'): void;
}

const emit = defineEmits<Emits>();

const props = defineProps<Props>();

const router = useRouter();

const statusTooltip = ref(null);
const isTooltipSet = ref(false);

const goToRequest = () => {
  router.push(`/mijn-aanvragen/${props.dossier.id}`);
};


const specificationsSpecificObject = [
  {
    icon: 'calendar-clock',
    title: 'Datum afspraak',
    value: props.dossier.properties[0].appointment_at ? props.dossier.properties[0].appointment_at : 'Geen afspraak gepland',
  },
  {
    icon: 'calendar-days',
    title: 'Gewenste passeerdatum',
    value: props.dossier.properties[0].passes_at ? dayjs().to(props.dossier.properties[0].passes_at) : '12-12-2024',
  },
  {
    icon: 'handshake',
    title: 'Definitieve passeerdatum',
    value: props.dossier.properties[0].passes_at ? dayjs().to(props.dossier.properties[0].passes_at) : '12-12-2024',
  },
];

const specifications = [
  {
    icon: 'user',
    title: 'Klant',
    value: props.dossier.properties[0].customer ? fullName(props.dossier.properties[0].customer) : null,
  },
  {
    icon: 'user-tie',
    title: 'Hypotheekacceptant',
    value: props.dossier.properties[0].accountManager ? `${props.dossier.properties[0].accountManager?.first_name} ${props.dossier.properties[0].accountManager?.last_name}` : 'Nader te benoemen',
  },
  {
    icon: 'clock-rotate-left',
    title: 'Laatste wijziging',
    value: props.dossier.properties[0].updated_at ?
      `${formatDateDutch(props.dossier.properties[0].updated_at)} om ${dayjs(props.dossier.properties[0].updated_at).format('HH:mm')}` : null,
  },
];

const filteredSpecifications = computed(() => specifications.filter(spec => spec.value));

const showTooltip = () => {
  if (!isTooltipSet.value) {
    useTooltip({ ref: statusTooltip, content: props.dossier.properties[0].advisor_status.tooltip ?? '' });
  }

  isTooltipSet.value = true;
};

const isAttentionNeeded = (properties: Property[]) => {
  for (let index = 0; index < properties.length; index++) {
    if (!properties[index].advisor_status.needs_attention) {
      return false;
    }
  }
  return true;
};
</script>

<template>
  <div
    :data-test-id="`dossier-request-component-${index}`"
    class="gap-x-2 items-center base:col-span-6 base:flex base:flex-wrap md:grid md:col-span-4 md:grid-cols-5"
  >
    <div class="flex justify-center">
      <FontAwesomeIcon
        :icon="['fad', 'folder-open']"
        size="xl"
        :data-test-id="`intermediair-dossier-request-open-close-icon-${props.index}`"
        @click="goToRequest()"
      />
    </div>
    <div class="flex flex-col md:!grid col-span-4 md:grid-cols-4">
      <div
        class="break-all hover:underline hover:cursor-pointer md:col-span-2"
        :data-test-id="`intermediair-dossier-request-id-${index.toString()}`"
        @click="goToRequest()"
      >
        {{ `D${dossier.id}` }}
      </div>
      <div class="whitespace-nowrap col-span-2 base:text-gray-700 md:text-inherit">
        {{ formatDateDutch(dossier.created_at) }}
      </div>
    </div>
  </div>
  <div class="flex flex-col base:row-start-2 base:py-4 base:col-span-12 md:col-span-3 md:py-0 md:row-start-auto">
    <span>Portefeuille aanvraag ({{ dossier.properties.length }} objecten)</span>
    <span class="text-xs text-gray-700 w-full">
      {{ dossier.properties[0].financing_type === FinancingType.BTL ? 'Verhuurhypotheek' : 'Ondernemershypotheek' }}
    </span>
  </div>
  <div
    class="base:row-start-3 base:col-span-12 base:text-blue-700 md:col-span-3 md:text-inherit md:row-start-auto flex justify-start items-center"
  >
    <div class="flex flex-col">
      <div>
        <span class="mr-2">
          {{ getLatestUpdatedStatus(dossier.properties) }}
        </span>
        <FontAwesomeIcon ref="statusTooltip" :icon="['fas', 'info-circle']" size="1x" class="text-blue-300" @mouseover="showTooltip()" @click.stop="() => null" />
      </div>
      <span
        v-if="isAttentionNeeded(props.dossier.properties)"
        class="text-xs text-orange-700"
      >
        Actie vereist
      </span>
      <span v-else class="text-xs text-gray-700">
        Geen actie vereist
      </span>
    </div>
  </div>
  <div class="flex justify-end gap-4 base:col-span-6 md:col-span-2 md:pr-4">
    <FontAwesomeIcon
      :icon="['fad', 'magnifying-glass']"
      size="xl"
      class="cursor-pointer text-2xl"
      :data-test-id="`intermediair-request-detail-icon-${props.index}`"
      @click="goToRequest()"
    />
    <FontAwesomeIcon
      class="cursor-pointer text-2xl"
      :class="{ 'rotate-180': props.isRowActive }"
      :icon="['fas', 'chevron-down']"
      size="xl"
      :data-test-id="`intermediair-request-open-icon-${props.index}`"
      @click.stop="emit('toggle:isRowActive')"
    />
  </div>
  <UISlideDown :is-active="props.isRowActive" class="col-span-12 grid grid-cols-12 gap-x-6 w-full" :class="{ 'mt-5': props.isRowActive }">
    <div v-if="dossier.properties" class="col-span-12 md:grid grid-cols-12 gap-x-6">
      <div
        v-if="dossier.properties[0].financing_type === FinancingType.P2P"
        class="grid border border-blue-300 rounded-lg p-5 gap-4 col-span-12 base:grid-cols-1 sm:grid-cols-2 md:!grid-cols-3 first:pl-3 mr-2 md:!mx-5"
      >
        <UISpecification
          v-for="(specification, specificationIndex) in filteredSpecifications"
          :key="specificationIndex"
          :label="specification.title"
          :icon="specification.icon"
          class="col-span-4"
        >
          <span v-if="specification.value" class="text-blue-700 font-semibold text-sm">
            {{ specification.value }}
          </span>
        </UISpecification>
      </div>
      <template v-for="(property, propertyIndex) in dossier.properties" :key="property.id">
        <div
          class="col-span-12 base:flex flex-col md:grid grid-cols-12
          gap-x-6 gap-y-4 md:gap-y-0 py-5 base:px-5 md:px-0 rounded-lg
          base:bg-white md:bg-transparent base:mr-2 md:mr-0 base:mb-4 md:mb-0"
        >
          <div class="gap-x-2 md:items-center base:col-span-6 base:flex base:flex-wrap base:flex-col md:grid md:col-span-4 md:grid-cols-5">
            <div
              class="col-start-2 col-span-2 items-center"
              :data-test-id="`intermediair-dossier-request-goto-button-${propertyIndex}`"
              @click="goToRequest()"
            >
              {{ property.id }}
            </div>
            <div class="col-span-2 items-center text-xs md:!text-sm text-gray-700 md:!text-inherit">
              {{ formatDateDutch(property.created_at) }}
            </div>
          </div>
          <div class="col-span-3 flex justify-center flex-col">
            <div v-if="property.entity.street_name !== '.'">
              {{ property.entity.street_name }} {{ property.entity.house_number }}{{ property.entity.house_number_addition ?? ''
              }}, {{ property.entity.city }}
            </div>

            <div v-else>Quickscan zonder adres</div>
            <span class="text-xs text-gray-700 w-full">{{ property.financing_type === FinancingType.BTL ? 'Verhuurhypotheek' : 'Ondernemershypotheek' }}</span>
          </div>
          <div
            class="base:row-start-3 base:col-span-12 base:text-blue-700 md:col-span-3 md:text-inherit md:row-start-auto flex justify-start items-center"
          >
            <div class="flex flex-col">
              <div>
                <span v-if="property.advisor_status && property.advisor_status.text" class="mr-2">{{ property.advisor_status.text }}</span>
              </div>
              <span
                :class="property.advisor_status.needs_attention ? 'text-orange-700' : 'text-gray-700'"
                class="text-xs"
              >
                {{ property.advisor_status.needs_attention ? 'Actie vereist' : 'Geen actie vereist' }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="property.financing_type === FinancingType.P2P"
          class="flex justify-between border col-span-12 border-gray-100 rounded-lg p-5 md:mx-4 my-6"
        >
          <UISpecification
            v-for="(specification, specificationIndex) in specificationsSpecificObject"
            :key="specificationIndex"
            :label="specification.title"
            :icon="specification.icon"
            class="col-span-4 flex justify-center items-center"
          >
            <span v-if="specification.value" class="text-blue-700 font-semibold text-sm">
              {{ specification.value }}
            </span>
          </UISpecification>
        </div>
      </template>
      <div
        v-if="dossier.properties[0].financing_type === FinancingType.BTL"
        class="grid border border-blue-300 rounded-lg p-5 gap-4 col-span-12 base:grid-cols-1 sm:grid-cols-2 md:!grid-cols-3 first:pl-3 mr-2 md:!mx-5"
      >
        <UISpecification
          v-for="(specification, specificationIndex) in filteredSpecifications"
          :key="specificationIndex"
          :label="specification.title"
          :icon="specification.icon"
        >
          <span v-if="specification.value" class="text-blue-700 font-semibold text-sm">
            {{ specification.value }}
          </span>
        </UISpecification>
      </div>
    </div>
    <div v-else>
      loading...
    </div>
  </UISlideDown>
</template>
